import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import AccountModal from '../components/Accounts/AccountModal';

const Accounts = ({ user }) => {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [showModal, setShowModal] = useState(false);

    // Состояние для пагинации
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 25,
        totalCount: 0,
        totalPages: 1
    });

    // Состояние для фильтров
    const [filters, setFilters] = useState({
        number: '',
        area: '',
        city: '',
        street: '',
        home: '',
        apartment: '',
        ownerName: '',
        ownerInn: ''
    });

    // Состояние формы
    const [showForm, setShowForm] = useState(false);
    const [editingAccount, setEditingAccount] = useState(null);
    const [formData, setFormData] = useState({
        number: '',
        area: '',
        address: {
            city: '',
            street: '',
            home: '',
            apartment: ''
        },
        persons: [{ name: '', inn: '' }]
    });
    const [message, setMessage] = useState('');

    // Мемоизированная функция загрузки данных
    const loadAccounts = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: pagination.page.toString(),
                pageSize: pagination.pageSize.toString(),
                ...filters
            });

            const response = await api.getAccountsWithPagination(params);
            if (response.data) {
                setAccounts(response.data.data || []);
                setPagination(prev => ({
                    ...prev,
                    totalCount: response.data.totalCount,
                    totalPages: response.data.totalPages
                }));
            }
        } catch (error) {
            setMessage('Ошибка загрузки лицевых счетов');
            console.error('Load accounts error:', error);
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.pageSize, filters]);

    // Загрузка данных при изменении страницы или фильтров
    useEffect(() => {
        loadAccounts();
    }, [loadAccounts]);

    // Обработчик двойного клика по строке таблицы
    const handleRowDoubleClick = async (account) => {
        try {
            setLoading(true);
            // Загружаем полные данные счета
            const accountData = await api.getAccountByNumber(account.number);
            setSelectedAccount(accountData.data);
            setShowModal(true);
        } catch (error) {
            setMessage('Ошибка загрузки данных счета');
            console.error('Error loading account details:', error);
        } finally {
            setLoading(false);
        }
    };

    // Обработчик изменения фильтров с сбросом на первую страницу
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Очистка всех фильтров
    const clearFilters = () => {
        setFilters({
            number: '',
            area: '',
            city: '',
            street: '',
            home: '',
            apartment: '',
            ownerName: '',
            ownerInn: ''
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

     // Функции форматирования
    const formatCurrency = (value) => {
        if (value === null || value === undefined || value === '') {
            return '-';
        }
        return new Intl.NumberFormat('ru-RU', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(Number(value));
    };

    // Навигация по страницам
    const goToPage = (page) => {
        if (page >= 1 && page <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, page }));
        }
    };

    // Обработчики для формы
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const accountData = {
                number: parseInt(formData.number),
                area: formData.area ? parseFloat(formData.area) : null,
                address: {
                    city: formData.address.city,
                    street: formData.address.street,
                    home: formData.address.home,
                    apartment: parseInt(formData.address.apartment) || 0
                },
                persons: formData.persons.filter(p => p.name).map(p => ({ ...p, inn: p.inn || '' }))
            };

            if (editingAccount) {
                await api.updateAccount(editingAccount.number, accountData);
                setMessage('Лицевой счет обновлен успешно');
            } else {
                await api.createAccount(accountData);
                setMessage('Лицевой счет создан успешно');
            }

            resetForm();
            loadAccounts();
        } catch (error) {
            setMessage('Ошибка сохранения лицевого счета');
            console.error('Save account error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (account) => {
        setEditingAccount(account);
        setFormData({
            number: account.number.toString(),
            area: account.area ? account.area.toString() : '',
            address: account.address || {
                city: '',
                street: '',
                home: '',
                apartment: ''
            },
            persons: account.persons && account.persons.length > 0
                ? account.persons
                : [{ name: '', inn: '' }]
        });
        setShowForm(true);
    };

    const handleDelete = async (number) => {
        if (!window.confirm('Вы уверены, что хотите удалить этот лицевой счет?')) {
            return;
        }

        try {
            await api.deleteAccount(number);
            setMessage('Лицевой счет удален успешно');
            loadAccounts();
        } catch (error) {
            setMessage('Ошибка удаления лицевого счета');
            console.error('Delete account error:', error);
        }
    };

    const resetForm = () => {
        setFormData({
            number: '',
            area: '',
            address: {
                city: '',
                street: '',
                home: '',
                apartment: ''
            },
            persons: [{ name: '', inn: '' }]
        });
        setEditingAccount(null);
        setShowForm(false);
        setMessage('');
    };

    const addPerson = () => {
        setFormData({
            ...formData,
            persons: [...formData.persons, { name: '', inn: '' }]
        });
    };

    const removePerson = (index) => {
        const newPersons = formData.persons.filter((_, i) => i !== index);
        setFormData({ ...formData, persons: newPersons });
    };

    const updatePerson = (index, field, value) => {
        const newPersons = [...formData.persons];
        newPersons[index][field] = value;
        setFormData({ ...formData, persons: newPersons });
    };

    const updateAddress = (field, value) => {
        setFormData({
            ...formData,
            address: {
                ...formData.address,
                [field]: value
            }
        });
    };

    // Генерация номеров страниц для пагинации
    const generatePageNumbers = () => {
        const pages = [];
        const current = pagination.page;
        const total = pagination.totalPages;

        if (total <= 7) {
            // Показываем все страницы если их мало
            for (let i = 1; i <= total; i++) {
                pages.push(i);
            }
        } else {
            // Всегда показываем первую страницу
            pages.push(1);

            // Показываем эллипсис если нужно
            if (current > 3) {
                pages.push('...');
            }

            // Показываем страницы вокруг текущей
            const start = Math.max(2, current - 1);
            const end = Math.min(total - 1, current + 1);

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            // Показываем эллипсис если нужно
            if (current < total - 2) {
                pages.push('...');
            }

            // Всегда показываем последнюю страницу
            pages.push(total);
        }

        return pages;
    };

    return (
        <div className="page">
            <h2>Лицевые счета</h2>

            {/* Панель управления */}
            <div className="page-header">
                {user && (user.role === 'admin' || user.role === 'lawyer') && (
                    <button
                        onClick={() => setShowForm(!showForm)}
                        className="btn-primary"
                    >
                        {showForm ? 'Отмена' : 'Добавить лицевой счет'}
                    </button>
                )}

                <button
                    onClick={loadAccounts}
                    className="btn-secondary"
                    disabled={loading}
                >
                    {loading ? 'Обновление...' : 'Обновить список'}
                </button>
            </div>

            {message && (
                <div className={`message ${message.includes('Ошибка') ? 'error' : 'success'}`}>
                    {message}
                </div>
            )}

            {/* Форма добавления/редактирования */}
            {showForm && (
                <div className="account-form-container">
                    <h3>{editingAccount ? 'Редактирование' : 'Создание'} лицевого счета</h3>

                    <form onSubmit={handleSubmit} className="account-form">
                        <div className="form-section">
                            <h4>Основная информация</h4>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Номер счета *</label>
                                    <input
                                        type="number"
                                        value={formData.number}
                                        onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                                        required
                                        disabled={!!editingAccount}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Площадь</label>
                                    <input
                                        type="number"
                                        step="any"
                                        value={formData.area}
                                        onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="form-section">
                            <h4>Адрес</h4>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Город *</label>
                                    <input
                                        type="text"
                                        value={formData.address.city}
                                        onChange={(e) => updateAddress('city', e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Улица *</label>
                                    <input
                                        type="text"
                                        value={formData.address.street}
                                        onChange={(e) => updateAddress('street', e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Дом *</label>
                                    <input
                                        type="text"
                                        value={formData.address.home}
                                        onChange={(e) => updateAddress('home', e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Квартира *</label>
                                    <input
                                        type="number"
                                        value={formData.address.apartment}
                                        onChange={(e) => updateAddress('apartment', e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="form-section">
                            <div className="section-header">
                                <h4>Собственники</h4>
                                <button type="button" onClick={addPerson} className="btn-add">
                                    + Добавить собственника
                                </button>
                            </div>

                            {formData.persons.map((person, index) => (
                                <div key={index} className="person-row">
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>ФИО *</label>
                                            <input
                                                type="text"
                                                value={person.name}
                                                onChange={(e) => updatePerson(index, 'name', e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>ИНН</label>
                                            <input
                                                type="text"
                                                value={person.inn}
                                                onChange={(e) => updatePerson(index, 'inn', e.target.value)}
                                            />
                                        </div>
                                        {formData.persons.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removePerson(index)}
                                                className="btn-remove"
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="form-actions">
                            <button type="submit" disabled={loading} className="btn-primary">
                                {loading ? 'Сохранение...' : (editingAccount ? 'Обновить' : 'Создать')}
                            </button>
                            <button type="button" onClick={resetForm} className="btn-secondary">
                                Отмена
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Панель фильтров */}
            <div className="filters-panel">
                <div className="filters-header">
                    <h4>Фильтры</h4>
                    <button
                        onClick={clearFilters}
                        className="btn-secondary btn-clear-filters"
                    >
                        Очистить фильтры
                    </button>
                </div>

                <div className="filters-grid">
                    {/* Номер счета - уменьшен на 30% */}
                    <div className="filter-group number">
                        <label>Номер счета</label>
                        <input
                            type="text"
                            value={filters.number}
                            onChange={(e) => handleFilterChange('number', e.target.value)}
                            placeholder="По номеру"
                        />
                    </div>

                     {/* ФИО владельца - увеличенная ширина */}
                    <div className="filter-group ownerName">
                        <label>ФИО владельца</label>
                        <input
                            type="text"
                            value={filters.ownerName}
                            onChange={(e) => handleFilterChange('ownerName', e.target.value)}
                            placeholder="По ФИО владельца"
                        />
                    </div>

                    {/* Площадь - уменьшена на 50% */}
                    <div className="filter-group area">
                        <label>Площадь</label>
                        <input
                            type="text"
                            value={filters.area}
                            onChange={(e) => handleFilterChange('area', e.target.value)}
                            placeholder="По площади"
                        />
                    </div>

                    {/* Город - стандартная ширина */}
                    <div className="filter-group city">
                        <label>Город</label>
                        <input
                            type="text"
                            value={filters.city}
                            onChange={(e) => handleFilterChange('city', e.target.value)}
                            placeholder="По городу"
                        />
                    </div>

                    {/* Улица - стандартная ширина */}
                    <div className="filter-group street">
                        <label>Улица</label>
                        <input
                            type="text"
                            value={filters.street}
                            onChange={(e) => handleFilterChange('street', e.target.value)}
                            placeholder="По улице"
                        />
                    </div>

                    {/* Дом - уменьшен на 50% */}
                    <div className="filter-group home">
                        <label>Дом</label>
                        <input
                            type="text"
                            value={filters.home}
                            onChange={(e) => handleFilterChange('home', e.target.value)}
                            placeholder="По дому"
                        />
                    </div>

                    {/* Квартира - уменьшена на 50% */}
                    <div className="filter-group apartment">
                        <label>Квартира</label>
                        <input
                            type="text"
                            value={filters.apartment}
                            onChange={(e) => handleFilterChange('apartment', e.target.value)}
                            placeholder="По квартире"
                        />
                    </div>

                    {/* ИНН владельца - стандартная ширина */}
                    <div className="filter-group ownerInn">
                        <label>ИНН владельца</label>
                        <input
                            type="text"
                            value={filters.ownerInn}
                            onChange={(e) => handleFilterChange('ownerInn', e.target.value)}
                            placeholder="По ИНН"
                        />
                    </div>
                </div>

                <div className="filter-stats">
                    Всего записей: {pagination.totalCount}
                    {Object.values(filters).some(filter => filter !== '') && (
                        <span className="filters-applied">(применены фильтры)</span>
                    )}
                </div>
            </div>

            {/* Пагинация сверху */}
            <div className="pagination-top">
                <div className="pagination-info">
                    Страница {pagination.page} из {pagination.totalPages}
                    ({pagination.totalCount} всего записей)
                </div>

                <div className="pagination-controls">
                    <button
                        onClick={() => goToPage(1)}
                        disabled={pagination.page === 1}
                        className="btn-pagination btn-pagination-first"
                    >
                        ⟪
                    </button>
                    <button
                        onClick={() => goToPage(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        className="btn-pagination btn-pagination-prev"
                    >
                        ⟨
                    </button>

                    {generatePageNumbers().map((pageNum, index) => (
                        pageNum === '...' ? (
                            <span key={index} className="pagination-ellipsis">...</span>
                        ) : (
                            <button
                                key={index}
                                onClick={() => goToPage(pageNum)}
                                className={`btn-pagination ${pagination.page === pageNum ? 'btn-pagination-active' : 'btn-pagination-inactive'}`}
                            >
                                {pageNum}
                            </button>
                        )
                    ))}

                    <button
                        onClick={() => goToPage(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                        className="btn-pagination btn-pagination-next"
                    >
                        ⟩
                    </button>
                    <button
                        onClick={() => goToPage(pagination.totalPages)}
                        disabled={pagination.page === pagination.totalPages}
                        className="btn-pagination btn-pagination-last"
                    >
                        ⟫
                    </button>
                </div>
            </div>

            {/* Таблица счетов */}
            <div className="accounts-table-container">
                <h3>Список лицевых счетов</h3>

                {loading && accounts.length === 0 ? (
                    <div className="loading">Загрузка...</div>
                ) : accounts.length === 0 ? (
                    <div className="no-data">
                        {Object.values(filters).some(filter => filter !== '')
                            ? 'Нет записей, соответствующих фильтрам'
                            : 'Нет данных о лицевых счетах'
                        }
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table className="accounts-table">
                            <thead>
                                <tr>
                                    <th>Номер счета</th>
                                    <th>Адрес</th>
                                    <th>Площадь</th>
                                    <th>Собственники</th>
                                    {/* <th className="actions-column">Действия</th> */}
                                </tr>
                            </thead>
                            <tbody>
                                {accounts.map(account => (
                                    <tr 
                                        key={account.number}
                                        onDoubleClick={() => handleRowDoubleClick(account)}
                                        style={{ cursor: 'pointer' }}
                                        className="account-row"
                                    >
                                        <td className="account-number">
                                            {account.number}
                                        </td>
                                        <td className="account-address">
                                            {account.address ? (
                                                <>
                                                    {account.address.city}, {account.address.street}, 
                                                    д. {account.address.home}
                                                    {account.address.apartment && account.address.apartment !== 0 
                                                        ? `, кв. ${account.address.apartment}` 
                                                        : ''
                                                    }
                                                </>
                                            ) : (
                                                <span className="no-address">Адрес не указан</span>
                                            )}
                                        </td>
                                         <td className="account-area">
                                            {formatCurrency(account.area) || '-'}
                                        </td>
                                        <td className="account-owners">
                                            <div className="owners-list">
                                                {account.persons && account.persons.length > 0 ? (
                                                    account.persons.map((person, index) => (
                                                        <div key={index} className="owner-item">
                                                            <div className="owner-name">{person.name}</div>
                                                            <div className="owner-inn">ИНН: {person.inn}</div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <span className="no-owners">Собственники не указаны</span>
                                                )}
                                            </div>
                                        </td>
                                        {/* <td className="account-actions">
                                            <div className="actions-buttons">
                                                {user && (user.role === 'admin' || user.role === 'lawyer') && (
                                                    <>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEdit(account);
                                                            }}
                                                            className="btn-edit"
                                                            title="Редактировать"
                                                        >
                                                            <span>✏️</span>
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDelete(account.number);
                                                            }}
                                                            className="btn-delete"
                                                            title="Удалить"
                                                        >
                                                            <span>🗑️</span>
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td> */}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Пагинация снизу */}
            <div className="pagination-bottom">
                <div className="pagination-info">
                    Страница {pagination.page} из {pagination.totalPages}
                    ({pagination.totalCount} всего записей)
                </div>

                <div className="pagination-controls">
                    <button
                        onClick={() => goToPage(1)}
                        disabled={pagination.page === 1}
                        className="btn-pagination btn-pagination-first"
                    >
                        ⟪
                    </button>
                    <button
                        onClick={() => goToPage(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        className="btn-pagination btn-pagination-prev"
                    >
                        ⟨
                    </button>

                    {generatePageNumbers().map((pageNum, index) => (
                        pageNum === '...' ? (
                            <span key={index} className="pagination-ellipsis">...</span>
                        ) : (
                            <button
                                key={index}
                                onClick={() => goToPage(pageNum)}
                                className={`btn-pagination ${pagination.page === pageNum ? 'btn-pagination-active' : 'btn-pagination-inactive'}`}
                            >
                                {pageNum}
                            </button>
                        )
                    ))}

                    <button
                        onClick={() => goToPage(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                        className="btn-pagination btn-pagination-next"
                    >
                        ⟩
                    </button>
                    <button
                        onClick={() => goToPage(pagination.totalPages)}
                        disabled={pagination.page === pagination.totalPages}
                        className="btn-pagination btn-pagination-last"
                    >
                        ⟫
                    </button>
                </div>
            </div>

            {/* Модальное окно с деталями счета */}
            {showModal && selectedAccount && (
                <AccountModal
                    account={selectedAccount}
                    onClose={() => {
                        setShowModal(false);
                        setSelectedAccount(null);
                    }}
                    onUpdate={loadAccounts}
                    user={user}
                />
            )}
        </div>
    );
};

export default Accounts;